import isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';

import { MED_DATE_ONLY_DISPLAY, TIME_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import type { ItemOutcome } from 'pages/open-course/common/models/types/ItemGrade';

import { getLabelForRuling } from 'bundles/author-assignment-grading/utils/plagiarismFlagUtils';
import { getPercentGradeFromDecimal } from 'bundles/author-common/utils/GradeUtils';
import type Item from 'bundles/author-course/models/Item';
import { getHoursAndMinutes } from 'bundles/author-course/utils/CourseMaterialUtils';
import type SessionItem from 'bundles/author-sessions/models/SessionItem';
import type { ItemGrade } from 'bundles/authoring/common/types/gradebookV2';
import type { CheatingCaseRuling } from 'bundles/naptimejs/resources/cheatingCaseStaffComments.v1';

import _t from 'i18n!nls/author-assignment-grading';

import { ChangeLogTypes } from '../constants/ChangeLog';

// date split into this custom format for readability - e.g. Apr 20, 2019 (4:20 PM PDT)
const getCustomDateFormat = (date: $TSFixMe) =>
  `${formatDateTimeDisplay(date, MED_DATE_ONLY_DISPLAY)} (${formatDateTimeDisplay(date, TIME_ONLY_DISPLAY)})`;

export type ProfileNames = {
  [userId: string]: string;
};

export type ItemLock = {
  unlockTime: number;
  relockTime: number;
};

export type UserItemAction = {
  definition: {
    timestamp: number;
    itemOutcome?: ItemOutcome;
    staffId?: number | string;
    reason?: string;
    explanation?: string;
    partnerExplanation?: string;
    comment?: string;
    cheatingCaseRuling?: CheatingCaseRuling;
    authorId?: string;
    oldDeadline?: number;
    newDeadline?: number;
    oldItemLock?: ItemLock;
    newItemLock?: ItemLock;
    oldTimeLimit?: number;
    newTimeLimit?: number;
    newMaxSubmissions?: number;
    oldMaxSubmissions?: number;
    oldMaxAttempts?: number;
    newMaxAttempts?: number;
  };
  typeName: keyof typeof ChangeLogTypes;
};

// modeled after UserActionItem.courier,
// see https://github.com/webedx-spark/infra-services/tree/main/libs/models/src/main/pegasus/org/coursera/ondemand/audittrail
export default class ChangeLogRecord {
  declare changeType: keyof typeof ChangeLogTypes;

  declare changeTimestamp: number;

  declare changeItemOutcome: ItemOutcome | null;

  declare itemGrade: ItemGrade | null;

  // @ts-expect-error TSMIGRATION
  declare grade: {
    displayGrade: string;
    isPassed: boolean;
  };

  declare changeStaffId: number | string | null;

  declare changeStaffFullName: string;

  declare changeReason: string | null;

  declare changeExplanation: string | null;

  declare changePartnerExplanation: string | null;

  declare cheatingCaseRuling: CheatingCaseRuling | null;

  declare oldDeadline: number | null;

  declare newDeadline: number | null;

  declare oldItemLock: ItemLock | null;

  declare newItemLock: ItemLock | null;

  declare oldTimeLimit: number | null;

  declare newTimeLimit: number | null;

  declare oldMaxSubmissions: number | null;

  declare newMaxSubmissions: number | null;

  declare oldMaxAttempts: number | null;

  declare newMaxAttempts: number | null;

  declare sessionStartDate: moment.Moment;

  declare sessionEndDate: moment.Moment;

  declare item: Item;

  constructor(
    { definition, typeName }: UserItemAction,
    profileNames: ProfileNames = {},
    currentItem: Item,
    sessionItem: SessionItem
  ) {
    // every change type has a typeName and timestamp
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'string' is not assignable to type '"itemProg... Remove this comment to see the full error message
    this.changeType = ChangeLogTypes[typeName];
    this.changeTimestamp = definition.timestamp;

    if (this.changeType === ChangeLogTypes.itemProgressStarted) {
      // [be-tech-debt] - https://coursera.atlassian.net/browse/PARTNER-14907
      // manually force `itemProgressStarted` to appear before `itemProgressComplete`
      // (we sort the events in the table by timestamp im milliseconds)
      this.changeTimestamp -= 1;
    }

    // additional optional info for override update/delete and grade hide/unhide change types
    this.changeItemOutcome = definition.itemOutcome || null;
    this.item = currentItem;

    // authorId exists for plagiarism events and is of format `USER~{userId}`
    const authorId =
      definition.authorId && definition.authorId.startsWith('USER~') && definition.authorId.split('~')[1];
    this.changeStaffId = authorId || definition.staffId || null;
    // absence of a user id implies an external agent made the change
    this.changeStaffFullName = this.changeStaffId ? profileNames[this.changeStaffId] : _t('Coursera Learner Support');

    this.changeExplanation = definition.explanation || null;
    this.changePartnerExplanation = definition.partnerExplanation || definition.comment || null;
    this.changeReason = definition.reason || null;

    // DEPRECATED: [FLEX-21803] support this event type until backfilled.
    // cheatingCaseRuling is a `string` for deprecated event `itemCheatingCaseUpdate`,
    // newer events will have `cheatingCaseRuling.ruling` as the string enum.
    this.cheatingCaseRuling =
      typeof definition.cheatingCaseRuling === 'string'
        ? definition.cheatingCaseRuling
        : // Dummy comment to make ts-expect-error work
          // @ts-expect-error TSMIGRATION
          (definition.cheatingCaseRuling || {}).ruling || null;

    // individual learner setting exceptions
    this.oldDeadline = definition.oldDeadline || null;
    this.newDeadline = definition.newDeadline || null;
    this.oldItemLock = definition.oldItemLock || null;
    this.newItemLock = definition.newItemLock || null;
    this.sessionStartDate = sessionItem.sessionStartDate;
    this.sessionEndDate = sessionItem.sessionEndDate;
    this.oldTimeLimit = definition.oldTimeLimit || null;
    this.newTimeLimit = definition.newTimeLimit || null;

    this.oldMaxSubmissions = definition.oldMaxSubmissions || null;
    this.newMaxSubmissions = definition.newMaxSubmissions || null;
    this.oldMaxAttempts = definition.oldMaxAttempts || null;
    this.newMaxAttempts = definition.newMaxAttempts || null;
  }

  get message() {
    const { fullName } = this.staffInfo;
    const { isAutoGraded } = this.item;
    const { oldDeadline, newDeadline } = this.deadlines;
    const { oldAvailability, newAvailability } = this.availabilities;
    const { oldTimeLimit, newTimeLimit } = this.timeLimits;
    const { oldMaxSubmissions, newMaxSubmissions } = this.submissionAttempts;
    const { oldMaxAttempts, newMaxAttempts } = this.maxAttempts;

    switch (this.changeType) {
      // item progress updates
      case ChangeLogTypes.itemProgressStarted:
        return _t('Learner started the assignment');
      case ChangeLogTypes.itemProgressComplete:
        if (!isAutoGraded) {
          return _t('Grade published to gradebook');
        }

        return _t('Assignment was graded');
      case ChangeLogTypes.ItemProgressSubmitted:
        return _t('Learner submitted assignment');
      case ChangeLogTypes.itemGradingComplete:
        return _t('#{fullName} saved the assignment grade', { fullName });
      case ChangeLogTypes.itemOutcomeOverrideUpdate:
        return _t('#{fullName} applied a grade override', { fullName });
      case ChangeLogTypes.itemOutcomeOverrideDelete:
        return _t('#{fullName} deleted a grade override', { fullName });
      case ChangeLogTypes.itemOutcomeHide:
        return _t('#{fullName} hid the assignment grade', { fullName });
      case ChangeLogTypes.itemOutcomeUnhide:
        return _t('#{fullName} unhid the assignment grade', { fullName });

      // individual learner exceptions
      case ChangeLogTypes.ItemDeadlineOverrideUpdate:
        return _t(
          '#{fullName} overrode the item deadline for this learner to #{newDeadline}. Previously was: #{oldDeadline}.',
          {
            fullName,
            oldDeadline,
            newDeadline,
          }
        );
      case ChangeLogTypes.ItemAvailabilityOverrideUpdate:
        return _t(
          '#{fullName} overrode the item availability for this learner to #{newAvailability}. Previously was: #{oldAvailability}.',
          {
            fullName,
            oldAvailability,
            newAvailability,
          }
        );

      case ChangeLogTypes.itemTimeLimitUpdate:
        return _t(
          '#{fullName} overrode the time limit per attempt for this learner to #{newTimeLimit}. Previously was #{oldTimeLimit}.',
          {
            fullName,
            oldTimeLimit,
            newTimeLimit,
          }
        );

      case ChangeLogTypes.itemMaxSubmissionPerTimedAttemptUpdate:
        return _t(
          '#{fullName} overrode the maximum number of submissions per timed attempt for this learner from #{oldMaxSubmissions} to #{newMaxSubmissions}.',
          {
            fullName,
            oldMaxSubmissions,
            newMaxSubmissions,
          }
        );

      case ChangeLogTypes.itemMaxAttemptsUpdate:
        return _t(
          '#{fullName} overrode the maximum number of attempts for this learner from #{oldMaxAttempts} to #{newMaxAttempts}.',
          {
            fullName,
            oldMaxAttempts,
            newMaxAttempts,
          }
        );

      // plagiarism updates
      case ChangeLogTypes.itemCheatingCaseStatusUpdate:
        return _t('#{fullName} updated the plagiarism flag status to "#{cheatingCaseRulingLabel}"', {
          fullName,
          cheatingCaseRulingLabel: this.cheatingCaseRulingLabel,
        });

      case ChangeLogTypes.itemCheatingCaseEvidenceUpdate:
        return _t('#{fullName} commented on the plagiarism flag status', { fullName });

      case ChangeLogTypes.itemCheatingCaseUpdate:
        // DEPRECATED: [FLEX-21803] support this event type until backfilled.
        // absence of a cheatingCaseRuling with an explanation implies a staff comment without change in flag status
        if (!this.cheatingCaseRuling && this.changePartnerExplanation) {
          return _t('#{fullName} commented on the plagiarism flag status', { fullName });
        }

        return _t('#{fullName} updated the plagiarism flag status to "#{cheatingCaseRulingLabel}"', {
          fullName,
          cheatingCaseRulingLabel: this.cheatingCaseRulingLabel,
        });

      case ChangeLogTypes.itemProgressReset:
        return this.changeReason
          ? _t('Assignment status has been reset to Not Submitted due to #{reason}', { reason: this.changeReason })
          : _t('Assignment status has been reset to Not Submitted');

      case ChangeLogTypes.itemGradeReset:
        return this.changeReason
          ? _t('Assignment grade has been removed due to #{reason}', { reason: this.changeReason })
          : _t('Assignment grade has been removed');

      case ChangeLogTypes.itemSubmissionUnsubmitted:
        return _t('#{fullName} unsubmitted the latest attempt', { fullName });

      default:
        return `Message: ${this.changeType}`;
    }
  }

  get explanation() {
    if (!this.changeExplanation) {
      return null;
    }

    return this.changeExplanation;
  }

  get partnerExplanation() {
    if (!this.changePartnerExplanation) {
      return null;
    }

    return this.changePartnerExplanation;
  }

  get type() {
    // type of change, from UserItemAction.courier
    return this.changeType;
  }

  // @ts-expect-error TSMIGRATION
  get grade() {
    if (!this.changeItemOutcome) {
      return null;
    }

    const { isPassed, grade } = this.changeItemOutcome;

    return {
      displayGrade: getPercentGradeFromDecimal(grade),
      isPassed,
    };
  }

  get cheatingCaseRulingLabel() {
    // @ts-expect-error TSMIGRATION
    return getLabelForRuling(this.cheatingCaseRuling);
  }

  get staffInfo() {
    return {
      id: this.changeStaffId,
      fullName: this.changeStaffFullName || '',
    };
  }

  get date() {
    return {
      timestamp: this.changeTimestamp,
      displayDate: formatDateTimeDisplay(this.changeTimestamp, MED_DATE_ONLY_DISPLAY),
      displayTime: formatDateTimeDisplay(this.changeTimestamp, TIME_ONLY_DISPLAY),
    };
  }

  get deadlines() {
    return {
      oldDeadline: this.oldDeadline ? getCustomDateFormat(this.oldDeadline) : null,
      newDeadline: this.newDeadline ? getCustomDateFormat(this.newDeadline) : null,
    };
  }

  get availabilities() {
    // Since item locks are relative durations from session start date, we calculate absolute values.
    // Absence of a new or old item lock or empty, means it was originally the session start/end date.
    const oldStartDate = !isEmpty(this.oldItemLock)
      ? moment(this.sessionStartDate).add((this.oldItemLock || {}).unlockTime)
      : moment(this.sessionStartDate);
    const oldEndDate = !isEmpty(this.oldItemLock)
      ? moment(this.sessionStartDate).add((this.oldItemLock || {}).relockTime)
      : moment(this.sessionEndDate);
    const newStartDate = !isEmpty(this.newItemLock)
      ? moment(this.sessionStartDate).add((this.newItemLock || {}).unlockTime)
      : moment(this.sessionStartDate);
    const newEndDate = !isEmpty(this.newItemLock)
      ? moment(this.sessionStartDate).add((this.newItemLock || {}).relockTime)
      : moment(this.sessionEndDate);

    const formattedOldStartDate = getCustomDateFormat(oldStartDate);
    const formattedOldEndDate = getCustomDateFormat(oldEndDate);
    const formattedNewStartDate = getCustomDateFormat(newStartDate);
    const formattedNewEndDate = getCustomDateFormat(newEndDate);

    return {
      oldAvailability: `${formattedOldStartDate} — ${formattedOldEndDate}`,
      newAvailability: `${formattedNewStartDate} — ${formattedNewEndDate}`,
    };
  }

  get timeLimits() {
    if (!this.oldTimeLimit || !this.newTimeLimit) {
      return {};
    }

    const { hours: oldHours, minutes: oldMinutes } = getHoursAndMinutes(this.oldTimeLimit);
    const { hours: newHours, minutes: newMinutes } = getHoursAndMinutes(this.newTimeLimit);
    return {
      oldTimeLimit: _t('#{oldHours} hours #{oldMinutes} minutes', { oldHours, oldMinutes }),
      newTimeLimit: _t('#{newHours} hours #{newMinutes} minutes', { newHours, newMinutes }),
    };
  }

  get submissionAttempts() {
    return {
      oldMaxSubmissions: this.oldMaxSubmissions,
      newMaxSubmissions: this.newMaxSubmissions,
    };
  }

  get maxAttempts() {
    return {
      oldMaxAttempts: this.oldMaxAttempts,
      newMaxAttempts: this.newMaxAttempts,
    };
  }
}
